import { createContext, useContext, useState, useCallback } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react'

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const addToast = useCallback((message, type = 'info', duration = 4500) => {
    const id = `${Date.now()}-${Math.random()}`
    setToasts(prev => [...prev.slice(-4), { id, message, type, duration }]) // max 5 toasts
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration)
    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastPortal toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

function ToastPortal({ toasts, onRemove }) {
  if (!toasts.length) return null

  const configs = {
    success: { bg: '#f0fdf4', border: '#22c55e', accent: '#16a34a', Icon: CheckCircle2 },
    error:   { bg: '#fef2f2', border: '#ef4444', accent: '#dc2626', Icon: XCircle },
    warning: { bg: '#fffbeb', border: '#f59e0b', accent: '#d97706', Icon: AlertTriangle },
    info:    { bg: '#eff6ff', border: '#3b82f6', accent: '#2563eb', Icon: Info },
  }

  return (
    <>
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(calc(100% + 32px)); opacity: 0; }
          to   { transform: translateX(0);                 opacity: 1; }
        }
        @keyframes toastProgress {
          from { transform: scaleX(1); }
          to   { transform: scaleX(0); }
        }
        .toast-item {
          animation: toastSlideIn 0.38s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .toast-item:hover { transform: translateY(-1px); }
        .toast-progress {
          animation: toastProgress linear forwards;
          transform-origin: left;
        }
      `}</style>
      <div style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 99999,
        display: 'flex', flexDirection: 'column-reverse', gap: 8,
        maxWidth: 400, width: 'calc(100vw - 48px)',
        pointerEvents: 'none',
      }}>
        {toasts.map(toast => {
          const cfg = configs[toast.type] || configs.info
          const ToastIcon = cfg.Icon
          const dur = toast.duration ?? 4500
          return (
            <div
              key={toast.id}
              className="toast-item"
              onClick={() => onRemove(toast.id)}
              role="alert"
              style={{
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderLeft: `4px solid ${cfg.accent}`,
                borderRadius: 14,
                padding: '13px 16px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 11,
                pointerEvents: 'auto',
                cursor: 'pointer',
                boxShadow: '0 4px 24px rgba(0,0,0,0.09), 0 1px 6px rgba(0,0,0,0.06)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <ToastIcon
                size={17}
                strokeWidth={2.5}
                style={{ color: cfg.accent, flexShrink: 0, marginTop: 1 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  margin: 0, color: '#111827',
                  fontSize: 13.5, lineHeight: 1.55,
                  fontWeight: 500,
                }}>{toast.message}</p>
              </div>
              <button
                onClick={e => { e.stopPropagation(); onRemove(toast.id) }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', fontSize: 20, padding: '0 0 0 6px',
                  lineHeight: 1, flexShrink: 0, fontWeight: 300,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#6b7280'}
                onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
              >×</button>
              {/* Progress bar */}
              <div
                className="toast-progress"
                style={{
                  position: 'absolute', bottom: 0, left: 0,
                  height: 3, width: '100%',
                  background: cfg.accent, opacity: 0.35,
                  animationDuration: `${dur}ms`,
                }}
              />
            </div>
          )
        })}
      </div>
    </>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
